<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import Modal from './Modal.vue'
import TextPreviewModal from './TextPreviewModal.vue'
import { Badge, Button, cx } from './ui'
import { IconUpload } from './Icons'
import { useToast } from '../lib/toast'
import { api, ApiError } from '../api/client'
import type { ShowAdminDetail, Taxonomy } from '../api/types'
import {buildImportPack,collectDroppedFiles,humanSize,packSummary,planSeasonBatches,submitImportPack,ImportStepError,type ImportPack,type SubDraft} from '../lib/importPack'
import { localeName } from '../lib/format'
import { useAsync } from '../lib/hooks'

const props = defineProps<{
  open: boolean
  lockedShowId?: number
}>()

const emit = defineEmits<{
  close: []
  done: [payload: { showId: number; focusSeason?: number; partial?: boolean }]
}>()

const toast = useToast()
const inputRef = ref<HTMLInputElement | null>(null)
const dirRef = ref<HTMLInputElement | null>(null)
const pack = ref<ImportPack | null>(null)
const error = ref('')
const busy = ref(false)
const progress = ref('')
const dragging = ref(false)
const preview = ref<SubDraft | null>(null)
const existingShow = ref<ShowAdminDetail | null>(null)
const overwriteAck = ref(false)
const showLookupDone = ref(true)
const partial = ref<{ showId: number; focusSeason?: number } | null>(null)
const draftGenres = ref<string[]>([])
const draftTropes = ref<string[]>([])
const pickingFiles = ref(false)
let pickingTimer: ReturnType<typeof setTimeout> | undefined
let pickingFocusHandler: (() => void) | undefined

const taxonomies = useAsync(() => api.listTaxonomies(), () => [props.open])
const genreOptions = computed(() => taxonomies.data.value?.taxonomies.filter((t) => t.kind === 'genre') ?? [])
const tropeOptions = computed(() => taxonomies.data.value?.taxonomies.filter((t) => t.kind === 'trope') ?? [])
const nameBySlug = computed(() => {
  const map = new Map<string, string>()
  for (const t of taxonomies.data.value?.taxonomies ?? []) map.set(`${t.kind}:${t.slug}`, t.name_zh)
  return map
})

function labelOf(kind: Taxonomy['kind'], slug: string): string {
  return nameBySlug.value.get(`${kind}:${slug}`) ?? slug
}

watch(
  () => props.open,
  (open) => {
    if (!open) return
    pack.value = null
    error.value = ''
    busy.value = false
    progress.value = ''
    dragging.value = false
    preview.value = null
    existingShow.value = null
    overwriteAck.value = false
    showLookupDone.value = true
    partial.value = null
    draftGenres.value = []
    draftTropes.value = []
    pickingFiles.value = false
  },
)

watch(dirRef, (el) => {
  if (el) el.setAttribute('webkitdirectory', '')
})

const summary = computed(() => (pack.value ? packSummary(pack.value) : null))
const batches = computed(() => (pack.value ? planSeasonBatches(pack.value.subs) : []))
const needsOverwriteAck = computed(
  () => props.lockedShowId == null && pack.value?.show != null && existingShow.value != null,
)
const submitLabel = computed(() => {
  if (!pack.value) return '导入'
  const hasShow = pack.value.show != null
  const hasSubs = pack.value.subs.length > 0
  const cover = needsOverwriteAck.value ? '覆盖' : '导入'
  if (hasShow && hasSubs) return `${cover}剧详情与字幕`
  if (hasShow) return `${cover}剧详情`
  if (hasSubs) return '导入字幕'
  return '导入'
})
const canSubmit = computed(() => {
  if (!pack.value) return false
  const hasShow = pack.value.show != null
  const hasSubs = pack.value.subs.length > 0
  if (!hasShow && !(props.lockedShowId != null && hasSubs)) return false
  if (!hasShow && !hasSubs) return false
  if (needsOverwriteAck.value && !overwriteAck.value) return false
  if (!showLookupDone.value) return false
  if (hasShow && draftGenres.value.length < 1) return false
  return true
})

const bySeason = computed(() => {
  if (!pack.value) return [] as { season: number; rows: SubDraft[] }[]
  const map = new Map<number, SubDraft[]>()
  for (const s of pack.value.subs) {
    const list = map.get(s.season) ?? []
    list.push(s)
    map.set(s.season, list)
  }
  return [...map.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([season, rows]) => ({ season, rows }))
})

function seedTaxonomies(next: ImportPack, existing: ShowAdminDetail | null) {
  if (next.show?.genres !== undefined) {
    draftGenres.value = [...next.show.genres]
  } else if (existing?.genres?.length) {
    draftGenres.value = [...existing.genres]
  } else {
    draftGenres.value = []
  }
  if (next.show?.tropes !== undefined) {
    draftTropes.value = [...next.show.tropes]
  } else if (existing?.tropes?.length) {
    draftTropes.value = [...existing.tropes]
  } else {
    draftTropes.value = []
  }
}

function toggleSlug(kind: 'genre' | 'trope', slug: string) {
  const list = kind === 'genre' ? draftGenres : draftTropes
  const max = kind === 'genre' ? 5 : 20
  const i = list.value.indexOf(slug)
  if (i >= 0) {
    list.value = list.value.filter((s) => s !== slug)
    return
  }
  if (list.value.length >= max) {
    toast.fail(`最多选 ${max} 个`)
    return
  }
  list.value = [...list.value, slug]
}

function endPickingSoon() {
  clearTimeout(pickingTimer)
  pickingTimer = setTimeout(() => {
    pickingFiles.value = false
  }, 400)
}

function openFilePicker(which: 'files' | 'dir') {
  pickingFiles.value = true
  if (pickingFocusHandler) window.removeEventListener('focus', pickingFocusHandler)
  pickingFocusHandler = () => {
    if (pickingFocusHandler) window.removeEventListener('focus', pickingFocusHandler)
    pickingFocusHandler = undefined
    endPickingSoon()
  }
  window.addEventListener('focus', pickingFocusHandler)
  if (which === 'files') inputRef.value?.click()
  else dirRef.value?.click()
}

onBeforeUnmount(() => {
  clearTimeout(pickingTimer)
  if (pickingFocusHandler) window.removeEventListener('focus', pickingFocusHandler)
})

async function lookupExistingShow(id: number): Promise<ShowAdminDetail | null> {
  try {
    return await api.showDetail(id)
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null
    throw err
  }
}

async function ingest(files: File[]) {
  pickingFiles.value = false
  if (files.length === 0) return
  error.value = ''
  existingShow.value = null
  overwriteAck.value = false
  showLookupDone.value = true
  pack.value = null
  partial.value = null
  draftGenres.value = []
  draftTropes.value = []
  try {
    const next = await buildImportPack(files, { lockedShowId: props.lockedShowId })
    pack.value = next
    if (!next.show && next.subs.length === 0) {
      error.value = next.skipped.length > 0 ? '没有可识别的 YAML 或字幕文件' : '未选中任何文件'
      return
    }
    if (!next.show && props.lockedShowId == null) {
      error.value = '列表导入需要 YAML（如 43.yml）以确定剧 ID'
      return
    }
    let existing: ShowAdminDetail | null = null
    if (next.show && props.lockedShowId == null) {
      showLookupDone.value = false
      existing = await lookupExistingShow(next.show.id)
      existingShow.value = existing
      showLookupDone.value = true
    }
    if (next.show) seedTaxonomies(next, existing)
  } catch (err) {
    error.value = err instanceof Error ? err.message : '解析失败'
  }
}

async function onDrop(e: DragEvent) {
  e.preventDefault()
  dragging.value = false
  if (!e.dataTransfer) return
  const files = await collectDroppedFiles(e.dataTransfer)
  await ingest(files)
}

async function submit() {
  if (!pack.value || !canSubmit.value) return
  if (pack.value.show && draftGenres.value.length < 1) {
    error.value = '请至少选择 1 个类型'
    return
  }
  busy.value = true
  error.value = ''
  try {
    const result = await submitImportPack(pack.value, {
      lockedShowId: props.lockedShowId,
      genres: pack.value.show ? draftGenres.value : undefined,
      tropes: pack.value.show ? draftTropes.value : undefined,
      onProgress: (p) => {
        progress.value = `${p.step} · ${p.done}/${p.total}`
      },
    })
    toast.ok(
      pack.value.subs.length > 0
        ? `剧 #${result.showId} · 已上传 ${result.uploaded} 条字幕`
        : `剧 #${result.showId} 详情已写入`,
    )
    emit('close')
    emit('done', { showId: result.showId })
  } catch (err) {
    if (err instanceof ImportStepError) {
      error.value = err.message
      partial.value = { showId: err.showId, focusSeason: err.failedSeason ?? undefined }
      toast.fail(`导入未完成 · 剧 #${err.showId}`)
    } else {
      error.value = err instanceof Error ? err.message : '导入失败'
    }
  } finally {
    busy.value = false
    progress.value = ''
  }
}

function episodesBySeason(rows: SubDraft[]) {
  const byEp = new Map<number, SubDraft[]>()
  for (const r of rows) {
    const list = byEp.get(r.episode) ?? []
    list.push(r)
    byEp.set(r.episode, list)
  }
  return [...byEp.entries()]
}

function requestClose() {
  if (!busy.value && !pickingFiles.value) emit('close')
}

function openPartialShow() {
  if (!partial.value) return
  emit('close')
  emit('done', { ...partial.value, partial: true })
}
</script>

<template>
  <Modal
    :open="open"
    :title="lockedShowId != null ? '导入到本剧' : '导入'"
    width="max-w-3xl"
    :suppress-close="pickingFiles"
    @close="requestClose"
  >
    <template #footer>
      <span class="mr-auto text-[12px] text-dim">
        {{
          progress ||
          (summary
            ? `${summary.files} 条字幕 · ${summary.episodes} 集 · ${humanSize(summary.bytes)}${
                batches.length > 1 ? ` · 分 ${batches.length} 次提交` : ''
              }`
            : '')
        }}
      </span>
      <Button :disabled="busy" @click="requestClose">取消</Button>
      <Button v-if="partial" :disabled="busy" @click="openPartialShow">查看剧 #{{ partial.showId }}</Button>
      <Button variant="primary" :loading="busy" :disabled="!canSubmit" @click="void submit()">
        <IconUpload :size="13" />
        {{ submitLabel }}
      </Button>
    </template>

    <div class="flex flex-col gap-4">
      <div
        :class="
          cx(
            'flex flex-col items-center gap-4 rounded-md border border-dashed px-6 py-8 transition-colors',
            dragging ? 'border-accent bg-accent/10' : 'border-line-strong bg-panel',
          )
        "
        @dragenter.prevent="dragging = true"
        @dragover.prevent
        @dragleave="dragging = false"
        @drop="void onDrop($event)"
      >
        <p class="text-center text-[13px] font-medium text-text">拖入文件夹，或分别选择文件</p>
        <div class="w-full max-w-sm space-y-1.5 text-[12px] leading-relaxed text-muted">
          <p>
            <span class="font-mono text-text">*.yml</span>
            <span class="mx-1.5 text-dim">→</span>
            剧详情（标题 / 简介；可选 genres / tropes）
          </p>
          <p>
            <span class="font-mono text-text">*.srt</span>
            <span class="mx-1.5 text-dim">→</span>
            字幕（如 <span class="font-mono">1.en.srt</span>）
          </p>
          <p v-if="lockedShowId == null" class="text-dim">新建剧需带 YAML；导入前须选类型</p>
          <p v-else class="text-dim">本剧 #{{ lockedShowId }}，可只传字幕</p>
        </div>
        <div class="flex flex-wrap items-center justify-center gap-2">
          <input
            ref="inputRef"
            type="file"
            multiple
            accept=".yml,.yaml,.srt,.vtt,.txt,.ass,.ssa"
            class="hidden"
            @change="
              (e) => {
                const t = e.target as HTMLInputElement
                void ingest(Array.from(t.files ?? []))
                t.value = ''
              }
            "
          />
          <input
            ref="dirRef"
            type="file"
            class="hidden"
            multiple
            @change="
              (e) => {
                const t = e.target as HTMLInputElement
                void ingest(Array.from(t.files ?? []))
                t.value = ''
              }
            "
          />
          <Button size="sm" variant="primary" @click="openFilePicker('files')">选择 yml / srt</Button>
          <Button size="sm" @click="openFilePicker('dir')">选择整剧文件夹</Button>
        </div>
      </div>

      <section v-if="pack?.show">
        <h3 class="col-head mb-2">剧 #{{ pack.show.id }}</h3>
        <label
          v-if="needsOverwriteAck"
          class="mb-2 flex items-start gap-2 rounded-md border border-warn/40 bg-warn/10 px-3 py-2 text-[12.5px] text-text"
        >
          <input v-model="overwriteAck" type="checkbox" class="mt-0.5" />
          <span>
            剧 #{{ pack.show.id }} 已存在（{{ existingShow?.locales.length ?? 0 }} 种语言 ·
            {{ existingShow?.episode_count ?? 0 }} 集 · {{ existingShow?.subtitle_count ?? 0 }} 条字幕）。导入将覆盖标题、简介与题材，请勾选确认。
          </span>
        </label>

        <div class="mb-3 rounded-md border border-line px-3 py-3">
          <div class="mb-2 text-[12px] font-medium text-text">题材</div>
          <div class="mb-3">
            <div class="mb-1.5 text-[11px] text-dim">类型（必选，最多 5 个）</div>
            <div class="flex flex-wrap gap-1.5">
              <button
                v-for="opt in genreOptions"
                :key="opt.slug"
                type="button"
                :class="
                  cx(
                    'rounded-sm border px-2 py-1 text-[12px] transition-colors',
                    draftGenres.includes(opt.slug)
                      ? 'border-accent/50 bg-accent/15 text-accent-dim'
                      : 'border-line bg-raised text-muted hover:border-accent/30',
                  )
                "
                @click="toggleSlug('genre', opt.slug)"
              >
                {{ opt.name_zh }}
              </button>
            </div>
            <p v-if="draftGenres.length < 1" class="mt-1.5 text-[11px] text-danger">请至少选择 1 个类型</p>
          </div>
          <div>
            <div class="mb-1.5 text-[11px] text-dim">标签（可选，最多 20 个）</div>
            <div class="flex flex-wrap gap-1.5">
              <button
                v-for="opt in tropeOptions"
                :key="opt.slug"
                type="button"
                :class="
                  cx(
                    'rounded-sm border px-2 py-1 text-[12px] transition-colors',
                    draftTropes.includes(opt.slug)
                      ? 'border-link/50 bg-link/10 text-link'
                      : 'border-line bg-raised text-muted hover:border-link/30',
                  )
                "
                @click="toggleSlug('trope', opt.slug)"
              >
                {{ opt.name_zh }}
              </button>
            </div>
          </div>
          <p
            v-if="draftGenres.length > 0 || draftTropes.length > 0"
            class="mt-2 flex flex-wrap gap-1 text-[11px] text-dim"
          >
            已选：
            <Badge v-for="g in draftGenres" :key="`dg-${g}`" tone="accent">{{ labelOf('genre', g) }}</Badge>
            <Badge v-for="t in draftTropes" :key="`dt-${t}`" tone="info">{{ labelOf('trope', t) }}</Badge>
          </p>
        </div>

        <ul class="divide-y divide-line rounded-md border border-line">
          <li v-for="l in pack.show.locales" :key="l.locale" class="px-3 py-2">
            <div class="flex items-baseline gap-2">
              <Badge tone="neutral">{{ l.locale }}</Badge>
              <span class="text-[12px] text-dim">{{ localeName(l.locale) }}</span>
              <span class="text-[13px] font-medium">{{ l.title }}</span>
            </div>
            <details v-if="l.description" class="mt-1" :open="pack!.show!.locales.length <= 3">
              <summary class="cursor-pointer text-[12px] text-dim">简介</summary>
              <p class="mt-1 whitespace-pre-wrap text-[12px] leading-relaxed text-muted">{{ l.description }}</p>
            </details>
          </li>
        </ul>
      </section>

      <section v-if="bySeason.length > 0" class="flex flex-col gap-3">
        <h3 class="col-head">字幕 · 点击语言可预览</h3>
        <div v-for="{ season, rows } in bySeason" :key="season" class="rounded-md border border-line">
          <div class="border-b border-line px-3 py-2 text-[12.5px] font-medium">
            第 {{ season }} 季 · {{ episodesBySeason(rows).length }} 集 · {{ rows.length }} 条
          </div>
          <ul class="max-h-56 overflow-y-auto divide-y divide-line">
            <li
              v-for="[ep, items] in episodesBySeason(rows)"
              :key="ep"
              class="flex items-center gap-2 px-3 py-1.5 text-[12px]"
            >
              <span class="tnum w-10 shrink-0 text-muted">E{{ String(ep).padStart(2, '0') }}</span>
              <span class="flex flex-wrap gap-1">
                <button
                  v-for="item in items"
                  :key="`${item.episode}-${item.locale}`"
                  type="button"
                  class="rounded-sm transition-opacity hover:opacity-80"
                  :title="`预览 ${item.fileName}`"
                  @click="preview = item"
                >
                  <Badge tone="info">{{ item.locale }}</Badge>
                </button>
              </span>
            </li>
          </ul>
        </div>
      </section>

      <p v-if="pack && pack.skipped.length > 0" class="text-[12px] text-dim">
        跳过 {{ pack.skipped.length }} 个：{{ pack.skipped.slice(0, 8).join('、')
        }}{{ pack.skipped.length > 8 ? '…' : '' }}
      </p>

      <p
        v-if="error"
        role="alert"
        class="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-[12.5px] text-danger"
      >
        {{ error }}
      </p>
    </div>
  </Modal>

  <TextPreviewModal
    :open="preview !== null"
    :title="
      preview
        ? `S${String(preview.season).padStart(2, '0')}E${String(preview.episode).padStart(2, '0')}.${preview.locale}`
        : ''
    "
    :hint="preview ? `${preview.fileName} · ${humanSize(preview.bytes)}` : undefined"
    :content="preview?.content ?? ''"
    @close="preview = null"
  />
</template>
